import { CreateTagButton } from "../tags/CreateTagButton";
import { Plus, X } from "lucide-react";

import { useGetList, useGetMany, useRecordContext, useUpdate } from "ra-core";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import type { Company, Tag } from "../types";

import { COMPANY_TAG_GROUPS, normalizeTagName } from "./companyTagGroups";

export const CompanyTagsEdit = ({
  record: recordProp,
}: {
  record?: Company;
}) => {
  const contextRecord = useRecordContext<Company>();

  const record = recordProp ?? contextRecord;

  const { data: allTags = [] } = useGetList<Tag>("tags", {
    pagination: {
      page: 1,
      perPage: 100,
    },

    sort: {
      field: "name",
      order: "ASC",
    },
  });

  const selectedIds = record?.tags ?? [];

  const { data: selectedTags = [] } = useGetMany<Tag>(
    "tags",
    {
      ids: selectedIds,
    },
    {
      enabled: selectedIds.length > 0,
    },
  );

  const [update] = useUpdate<Company>();

  if (!record) {
    return null;
  }

  const availableTags = allTags.filter((tag) => !selectedIds.includes(tag.id));
  const groups = [
    ...COMPANY_TAG_GROUPS,
    {
      key: "custom",
      title: "Другие",
      names: availableTags
        .filter(
          (tag) =>
            !COMPANY_TAG_GROUPS.some((group) =>
              group.names.some(
                (name) => normalizeTagName(name) === normalizeTagName(tag.name),
              ),
            ),
        )
        .map((tag) => tag.name),
    },
  ];

  const addTag = (id: number) => {
    update(
      "companies",
      {
        id: record.id,

        data: {
          tags: [...selectedIds, id],
        },

        previousData: record,
      },
      {
        mutationMode: "optimistic",
      },
    );
  };

  const removeTag = (id: number) => {
    update(
      "companies",
      {
        id: record.id,

        data: {
          tags: selectedIds.filter((tagId) => tagId !== id),
        },

        previousData: record,
      },
      {
        mutationMode: "optimistic",
      },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CreateTagButton onCreated={(tag) => addTag(tag.id)} />
      {selectedTags.map((tag) => (
        <button
          key={tag.id}
          type="button"
          onClick={() => removeTag(tag.id)}
          title={`Убрать тег «${tag.name}»`}
          className="rounded-full"
        >
          <Badge
            variant="secondary"
            className="cursor-pointer gap-1.5 font-normal rovero-colored-tag text-[#482719]"
            style={{
              backgroundColor: tag.color,
            }}
          >
            {tag.name}

            <X className="h-3 w-3" />
          </Badge>
        </button>
      ))}

      {availableTags.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-7">
              <Plus className="h-3.5 w-3.5" />
              Добавить
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="w-64 max-h-[420px] overflow-y-auto"
          >
            {groups.map((group) => {
              const groupTags = availableTags.filter((tag) =>
                group.names.some(
                  (name) =>
                    normalizeTagName(name) === normalizeTagName(tag.name),
                ),
              );

              if (groupTags.length === 0) {
                return null;
              }

              return (
                <div key={group.key} className="py-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {group.title}
                  </div>

                  {groupTags.map((tag) => (
                    <DropdownMenuItem
                      key={tag.id}
                      onSelect={() => addTag(tag.id)}
                    >
                      <Badge
                        variant="secondary"
                        className="font-normal rovero-colored-tag text-[#482719]"
                        style={{
                          backgroundColor: tag.color,
                        }}
                      >
                        {tag.name}
                      </Badge>
                    </DropdownMenuItem>
                  ))}
                </div>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {selectedTags.length === 0 && availableTags.length === 0 && (
        <span className="text-sm text-muted-foreground">Нет тегов</span>
      )}
    </div>
  );
};
